<template>
    <div id="movements-page">
        <div class="page-header">
            <div class="header-left">
                <div class="header-icon">
                    <span class="material-icons">history</span>
                </div>
                <div class="header-info">
                    <h2>{{ $t('whatsapp_business_api.wabaMovementsTitle') }}</h2>
                    <p>{{ $t('whatsapp_business_api.wabaMovementsSubtitle') }}</p>
                </div>
            </div>
            <div class="header-right">
                <div class="activity-count" v-if="pagination.total > 0">
                    <span class="count">{{ pagination.total }}</span>
                    <span class="label">{{ $t('whatsapp_business_api.wabaMovementsTotalActivity') }}</span>
                </div>
                <button class="refresh-btn" @click="refreshActivities" :disabled="loading">
                    <span class="material-icons" :class="{ spinning: loading }">refresh</span>
                </button>
            </div>
        </div>
        
        <div class="notification-settings" :class="{ collapsed: !settingsExpanded }">
            <div class="settings-header" @click="settingsExpanded = !settingsExpanded">
                <div class="header-left">
                    <span class="material-icons header-icon">notifications</span>
                    <span class="header-title">{{ $t('whatsapp_business_api.wabaMovementsEmailNotifications') }}</span>
                    <span class="status-badge" :class="{ active: notificationsEnabled }">
                        {{ notificationsEnabled ? $t('whatsapp_business_api.wabaMovementsActive') : $t('whatsapp_business_api.wabaMovementsOff') }}
                    </span>
                </div>
                <span class="material-icons expand-icon" :class="{ expanded: settingsExpanded }">expand_more</span>
            </div>
            
            <div class="settings-content" v-show="settingsExpanded">
                <div class="setting-item">
                    <div class="setting-info">
                        <span class="material-icons">email</span>
                        <div class="setting-text">
                            <span class="setting-title">{{ $t('whatsapp_business_api.wabaMovementsEmailSettingTitle') }}</span>
                            <span class="setting-desc">{{ $t('whatsapp_business_api.wabaMovementsEmailSettingDesc') }}</span>
                        </div>
                    </div>
                    <a-switch 
                        :checked="notificationsEnabled" 
                        :loading="updateLoading"
                        @change="toggleNotifications" 
                    />
                </div>
                
                <!-- Email List Management -->
                <div class="email-management" v-if="notificationsEnabled">
                    <div class="email-header">
                        <span class="material-icons">people</span>
                        <span>{{ $t('whatsapp_business_api.wabaMovementsEmailListHeader') }}</span>
                    </div>
                    <div class="email-input-wrapper">
                        <a-input 
                            v-model="newEmail" 
                            :placeholder="$t('whatsapp_business_api.wabaMovementsEmailPlaceholder')"
                            @pressEnter="addEmail"
                            :disabled="updateLoading"
                        />
                        <a-button 
                            type="primary" 
                            @click="addEmail" 
                            :disabled="!isValidEmail || updateLoading"
                        >
                            <span class="material-icons">add</span>
                        </a-button>
                    </div>
                    <div class="email-tags">
                        <a-tag 
                            v-for="email in notificationEmails" 
                            :key="email"
                            closable
                            @close="removeEmail(email)"
                        >
                            {{ email }}
                        </a-tag>
                        <span class="no-emails" v-if="notificationEmails.length === 0">
                            {{ $t('whatsapp_business_api.wabaMovementsNoEmailsYet') }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="page-content">
            <WabaActivityListView 
                :activities="activities" 
                :loading="loading" 
                :hasMore="pagination.hasNextPage"
                @load-more="loadMore" 
            />
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import WabaActivityListView from "./WabaActivityListView.vue";
import { mapActions, mapState } from "vuex";
import _ from "lodash";

export default Vue.extend({
    name: "MovementsPage",
    components: {
        WabaActivityListView,
    },
    props: {
        channel: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            cursor: {
                page: 1,
                limit: 50,
            },
            updateLoading: false,
            newEmail: "",
            settingsExpanded: false,
        };
    },
    computed: {
        ...mapState({
            activities: (state) => state.whatsapp.wabaActivityLogs,
            loading: (state) => state.whatsapp.wabaActivityLogsLoading,
            pagination: (state) => state.whatsapp.wabaActivityLogsPagination,
        }),
        notificationsEnabled() {
            const notifications = this.$_.get(this.channel, "accountNotifications", { enabled: false, emails: [] });
            return notifications.enabled || false;
        },
        notificationEmails() {
            const notifications = this.$_.get(this.channel, "accountNotifications", { enabled: false, emails: [] });
            return notifications.emails || [];
        },
        isValidEmail() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return this.newEmail && emailRegex.test(this.newEmail.trim());
        },
    },
    methods: {
        ...mapActions({
            getWabaAccountActivityLog: "whatsapp/getWabaAccountActivityLog",
            clearWabaActivityLogs: "whatsapp/clearWabaActivityLogs",
            updateChannel: "channel/updateChannel",
        }),
        fetchActivities(append = false) {
            if (!this.channel || !this.channel._id) {
                return;
            }

            this.getWabaAccountActivityLog({
                channelId: this.channel._id,
                page: this.cursor.page,
                limit: this.cursor.limit,
                append,
            }).catch((err) => {
                console.error("Failed to fetch WABA activity logs:", err);
                this.$message.error(this.$t("whatsapp_business_api.wabaMovementsActivityLoadError"));
            });
        },
        loadMore() {
            if (this.pagination.hasNextPage && !this.loading) {
                this.cursor.page = this.cursor.page + 1;
                this.fetchActivities(true);
            }
        },
        refreshActivities() {
            this.cursor.page = 1;
            this.fetchActivities();
        },
        async toggleNotifications(checked) {
            if (!this.channel || !this.channel._id) return;
            
            this.updateLoading = true;
            try {
                // Mevcut accountNotifications değerlerini al, yoksa default değerler kullan
                const currentNotifications = this.$_.get(this.channel, "accountNotifications", {});
                const payload = {
                    ...this.channel,
                    accountNotifications: {
                        enabled: checked,
                        emails: currentNotifications.emails || [],
                    },
                };
                await this.updateChannel(payload);
                this.$emit("refresh");
                this.$message.success(checked ? this.$t("whatsapp_business_api.wabaMovementsNotificationsOn") : this.$t("whatsapp_business_api.wabaMovementsNotificationsOff"));
            } catch (err) {
                console.error("Failed to update channel:", err);
                this.$message.error(this.$t("whatsapp_business_api.wabaMovementsUpdateError"));
            } finally {
                this.updateLoading = false;
            }
        },
        async addEmail() {
            if (!this.isValidEmail || !this.channel || !this.channel._id) return;
            
            const email = this.newEmail.trim().toLowerCase();
            const currentNotifications = this.$_.get(this.channel, "accountNotifications", { enabled: false, emails: [] });
            
            // Email zaten listede varsa uyarı ver
            if (currentNotifications.emails && currentNotifications.emails.includes(email)) {
                this.$message.warning(this.$t("whatsapp_business_api.wabaMovementsEmailAlreadyAdded"));
                this.newEmail = "";
                return;
            }
            
            this.updateLoading = true;
            try {
                const updatedEmails = [...(currentNotifications.emails || []), email];
                const payload = {
                    ...this.channel,
                    accountNotifications: {
                        enabled: true,
                        emails: updatedEmails,
                    },
                };
                await this.updateChannel(payload);
                this.$emit("refresh");
                this.newEmail = "";
                this.$message.success(this.$t("whatsapp_business_api.wabaMovementsEmailAdded"));
            } catch (err) {
                console.error("Failed to add email:", err);
                this.$message.error(this.$t("whatsapp_business_api.wabaMovementsEmailAddError"));
            } finally {
                this.updateLoading = false;
            }
        },
        async removeEmail(email) {
            if (!this.channel || !this.channel._id) return;
            
            const currentNotifications = this.$_.get(this.channel, "accountNotifications", { enabled: true, emails: [] });
            const updatedEmails = (currentNotifications.emails || []).filter(e => e !== email);
            
            this.updateLoading = true;
            try {
                const payload = {
                    ...this.channel,
                    accountNotifications: {
                        enabled: true,
                        emails: updatedEmails,
                    },
                };
                await this.updateChannel(payload);
                this.$emit("refresh");
                this.$message.success(this.$t("whatsapp_business_api.wabaMovementsEmailRemoved"));
            } catch (err) {
                console.error("Failed to remove email:", err);
                this.$message.error(this.$t("whatsapp_business_api.wabaMovementsEmailRemoveError"));
            } finally {
                this.updateLoading = false;
            }
        },
    },
    mounted() {
        if (this.channel && this.channel._id) {
            this.fetchActivities();
        }
    },
    watch: {
        channel: {
            handler(newVal, oldVal) {
                if (newVal && newVal._id) {
                    if (oldVal && oldVal._id !== newVal._id) {
                        this.clearWabaActivityLogs();
                    }
                    this.cursor.page = 1;
                    this.fetchActivities();
                } else if (!newVal || !newVal._id) {
                    this.clearWabaActivityLogs();
                }
            },
            immediate: true,
        },
    },
});
</script>

<style scoped lang="scss">
#movements-page {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: var(--color-bg-default);

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 24px;
        border-bottom: 1px solid var(--color-border-default);
        background: var(--color-bg-default);

        .header-left {
            display: flex;
            align-items: center;
            gap: 16px;

            .header-icon {
                width: 48px;
                height: 48px;
                border-radius: 12px;
                background: linear-gradient(135deg, #25D366, #128C7E);
                display: flex;
                align-items: center;
                justify-content: center;

                .material-icons {
                    font-size: 24px;
                    color: white;
                }
            }

            .header-info {
                h2 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: var(--color-text-default);
                }

                p {
                    margin: 4px 0 0;
                    font-size: 13px;
                    color: var(--color-text-secondary);
                }
            }
        }

        .header-right {
            display: flex;
            align-items: center;
            gap: 16px;

            .activity-count {
                display: flex;
                flex-direction: column;
                align-items: flex-end;

                .count {
                    font-size: 20px;
                    font-weight: 700;
                    color: var(--color-text-default);
                }

                .label {
                    font-size: 11px;
                    color: var(--color-text-secondary);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
            }

            .refresh-btn {
                width: 40px;
                height: 40px;
                border-radius: 10px;
                border: 1px solid var(--color-border-default);
                background: var(--color-bg-default);
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s ease;

                &:hover:not(:disabled) {
                    background: var(--color-bg-neutral-subtle);
                    border-color: var(--color-border-strong);
                }

                &:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .material-icons {
                    font-size: 20px;
                    color: var(--color-icon-default);

                    &.spinning {
                        animation: spin 1s linear infinite;
                    }
                }
            }
        }
    }

    .notification-settings {
        border-bottom: 1px solid var(--color-border-default);
        background: var(--color-bg-default);

        .settings-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 24px;
            cursor: pointer;
            transition: background 0.2s ease;
            user-select: none;

            &:hover {
                background: var(--color-bg-subtle);
            }

            .header-left {
                display: flex;
                align-items: center;
                gap: 10px;

                .header-icon {
                    font-size: 20px;
                    color: #25D366;
                }

                .header-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: var(--color-text-default);
                }

                .status-badge {
                    padding: 2px 8px;
                    border-radius: 10px;
                    font-size: 11px;
                    font-weight: 500;
                    background: var(--color-bg-neutral-subtle);
                    color: var(--color-text-secondary);

                    &.active {
                        background: rgba(37, 211, 102, 0.15);
                        color: #25D366;
                    }
                }
            }

            .expand-icon {
                font-size: 20px;
                color: var(--color-icon-default);
                transition: transform 0.2s ease;

                &.expanded {
                    transform: rotate(180deg);
                }
            }
        }

        .settings-content {
            padding: 0 24px 16px 24px;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        &.collapsed {
            .settings-header {
                border-bottom: none;
            }
        }

        .setting-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 14px 16px;
            background: var(--color-bg-subtle);
            border: 1px solid var(--color-border-default);
            border-radius: 8px;
            transition: all 0.2s ease;

            &:hover {
                border-color: var(--color-border-strong);
            }
            
            .setting-info {
                display: flex;
                align-items: center;
                gap: 12px;

                .material-icons {
                    font-size: 22px;
                    color: #25D366;
                }

                .setting-text {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;

                    .setting-title {
                        font-size: 14px;
                        font-weight: 600;
                        color: var(--color-text-default);
                    }

                    .setting-desc {
                        font-size: 12px;
                        color: var(--color-text-secondary);
                    }
                }
            }
        }

        .email-management {
            padding: 14px 16px;
            background: var(--color-bg-subtle);
            border: 1px solid var(--color-border-default);
            border-radius: 8px;

            .email-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 12px;
                padding-bottom: 10px;
                border-bottom: 1px solid var(--color-border-default);
                font-size: 13px;
                font-weight: 600;
                color: var(--color-text-default);

                .material-icons {
                    font-size: 18px;
                    color: #25D366;
                }
            }

            .email-input-wrapper {
                display: flex;
                gap: 8px;
                margin-bottom: 12px;

                .ant-input {
                    flex: 1;
                    max-width: 400px;
                    border-radius: 6px;
                    height: 36px;
                    font-size: 13px;
                }

                .ant-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                    min-width: 36px;
                    height: 36px;

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }

            .email-tags {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                min-height: 40px;
                padding: 10px;
                background: var(--color-bg-default);
                border: 1px dashed var(--color-border-default);
                border-radius: 6px;

                .ant-tag {
                    margin: 0;
                    padding: 6px 12px;
                    font-size: 13px;
                    border-radius: 6px;
                    background: #25D366;
                    border-color: transparent;
                    color: white;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    transition: all 0.2s ease;

                    &:hover {
                        opacity: 0.9;
                    }

                    .anticon-close {
                        color: white;
                        font-size: 11px;
                    }
                }

                .no-emails {
                    font-size: 12px;
                    color: var(--color-text-secondary);
                    font-style: italic;
                    padding: 6px;
                }
            }
        }
    }

    .page-content {
        flex: 1;
        padding: 24px 24px 0px 24px;
        overflow: hidden;
        min-height: 0;
    }
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
